import { bind, classNames, Component, h, signal, tag } from "omi";

import { tailwind } from "@/tailwind";

type Props = {
  onNotReceived: () => void;
  onChange: (code: string) => void;
  error?: string;
};

@tag("nw-otp-form")
export class NWOtpForm extends Component<Props> {
  static css = [tailwind];

  static propTypes = {
    onChange: Function,
    onNotReceived: Function,
    error: String,
  };

  private inputNum = signal(Array.from({ length: 6 }));

  installed() {
    const form = this.shadowRoot?.getElementById("otp-form");

    if (form == null) {
      return;
    }

    const inputs = Array.from(form.querySelectorAll("input[type=text]").values()) as HTMLInputElement[];

    const handleKeyDown = (e: HTMLElementEventMap["keydown"]) => {
      if (!/^[0-9]{1}$/.test(e.key) && e.key !== "Backspace" && e.key !== "Delete" && e.key !== "Tab" && !e.metaKey) {
        e.preventDefault();
      }

      if (e.key === "Delete" || e.key === "Backspace") {
        const index = inputs.indexOf(e.target as HTMLInputElement);
        if (index > 0) {
          inputs[index - 1].value = "";
          inputs[index].value = "";
          inputs[index - 1].focus();
        }
      }
    };

    const getValue = () => {
      return inputs.map(input => input.value).join("");
    };

    const handleInput = (event: HTMLElementEventMap["input"]) => {
      const target = event.target as HTMLInputElement | null;
      if (target == null) {
        return;
      }

      const index = inputs.indexOf(target);
      if (target.value) {
        if (index < inputs.length - 1) {
          inputs[index + 1].focus();
        } else {
          this.props.onChange(getValue());
        }
      }
    };

    const handleFocus = (event: HTMLElementEventMap["focus"]) => {
      const target = event.target as HTMLInputElement | null;
      target?.select();
    };

    const handlePaste = (event: HTMLElementEventMap["paste"]) => {
      event.preventDefault();
      const text = event.clipboardData?.getData("text");

      if (text == null) {
        return;
      }

      if (!new RegExp(`^[0-9]{${inputs.length}}$`).test(text)) {
        return;
      }

      const digits = text.split("");
      inputs.forEach((input, index) => (input.value = digits[index]));
      this.props.onChange(getValue());
    };

    inputs.forEach(input => {
      input.addEventListener("input", handleInput);
      input.addEventListener("keydown", handleKeyDown);
      input.addEventListener("focus", handleFocus);
      input.addEventListener("paste", handlePaste);
    });
  }

  @bind
  private handleNotReceived(e: Event) {
    e.preventDefault();
    this.props.onNotReceived();
  }

  render({ error }: Props) {
    return (
      <div id="otp-form" class="flex flex-col items-center">
        <div class="flex items-center justify-center space-x-3 w-full mb-2">
          {this.inputNum.value.map((_, index) => (
            <input
              key={index}
              required
              type="text"
              inputmode="numeric"
              autocomplete="one-time-code"
              class={classNames(
                "w-full h-14 sm:w-14 sm:h-14 shadow-xs text-center text-lg md:text-2xl font-semibold text-primary rounded-lg border transition-all p-2 md:p-4 focus:outline-none focus:bg-white",
                {
                  "!border-error-300 focus:shadow-xse focus:border-error-600": !!error,
                  "!border-gray-300 focus:shadow-xss focus:!border-brand-300": !error,
                },
              )}
              pattern="\d*"
              maxlength="1"
            />
          ))}
        </div>
        {!!error && <p class="text-sm text-error-600">{error}</p>}
        {!error && (
          <button class="text-tertiary text-sm underline p-1" onClick={this.handleNotReceived}>
            <span>Did not get the code?</span>
          </button>
        )}
      </div>
    );
  }
}
